// frontend/src/services/TierLimitService.js
// TIER-BASED RECORDING LIMITS - BULLETPROOF ENFORCEMENT
// GPT5 SPECIFICATION: Enforce limits BEFORE recording, not after failure

class TierLimitService {
  constructor() {
    // SIMPLIFIED: Only Free and Pro tiers
    // Voice recording duration fetched from backend via setVoiceRecordingTierLimits()
    this.TIER_LIMITS = {
      free: {
        maxRecordingSec: 0, // No voice recording for free tier
        maxFileSize: 20 * 1024 * 1024, // 20MB
        maxMonthlyMinutes: 0, // No voice for free
        features: []
      },
      pro: {
        maxRecordingSec: 3600, // Will be overridden by backend (default: 60 min)
        maxFileSize: 200 * 1024 * 1024, // 200MB
        maxMonthlyMinutes: 24 * 60, // 24 hours per month (1440 min)
        features: ['enhanced_transcription', 'diarization', 'real_time']
      }
    };

    this.currentUsage = {
      monthlyMinutesUsed: 0,
      lastReset: new Date().getMonth()
    };

    this.loadUsageFromStorage();
  }

  /**
   * Update voice recording duration limits from backend
   * Called after fetching /api/settings/public
   * SIMPLIFIED: Only updates free and pro tiers
   * @param {Object} tierMap - { free: 0, pro: 3600 }
   */
  setVoiceRecordingTierLimits(tierMap) {
    console.log('[TierLimitService] Updating voice recording limits from backend:', tierMap);

    // Handle deprecated tiers: map them to free/pro
    const normalizedMap = {};
    Object.keys(tierMap).forEach(tier => {
      let normalizedTier = tier;
      if (tier === 'starter') normalizedTier = 'free';
      if (tier === 'max') normalizedTier = 'pro';
      normalizedMap[normalizedTier] = tierMap[tier];
    });

    // Update only free and pro
    if (normalizedMap.free !== undefined && this.TIER_LIMITS.free) {
      this.TIER_LIMITS.free.maxRecordingSec = normalizedMap.free;
    }
    if (normalizedMap.pro !== undefined && this.TIER_LIMITS.pro) {
      this.TIER_LIMITS.pro.maxRecordingSec = normalizedMap.pro;
    }

    console.log('[TierLimitService] Updated limits:', {
      free: this.TIER_LIMITS.free.maxRecordingSec,
      pro: this.TIER_LIMITS.pro.maxRecordingSec
    });
  }

  /**
   * Check if user can start a new recording
   * CRITICAL: Must be called BEFORE recording starts
   */
  canStartRecording(userTier = 'free', estimatedDurationSec = 0) {
    const limits = this.getTierLimits(userTier);
    const issues = [];

    // Check single recording duration limit
    if (estimatedDurationSec > limits.maxRecordingSec) {
      issues.push({
        type: 'duration_exceeded',
        message: `Recording duration (${this.formatTime(estimatedDurationSec)}) exceeds ${userTier} tier limit of ${this.formatTime(limits.maxRecordingSec)}`,
        limit: limits.maxRecordingSec,
        requested: estimatedDurationSec
      });
    }

    // Check monthly usage limit (if not unlimited)
    if (limits.maxMonthlyMinutes > 0) {
      const remainingMinutes = this.getRemainingMonthlyMinutes(userTier);
      const requestedMinutes = Math.ceil(estimatedDurationSec / 60);

      if (requestedMinutes > remainingMinutes) {
        issues.push({
          type: 'monthly_limit_exceeded',
          message: `Recording would exceed monthly limit. Remaining: ${remainingMinutes} minutes, Requested: ${requestedMinutes} minutes`,
          remainingMinutes,
          requestedMinutes
        });
      }
    }

    return {
      canRecord: issues.length === 0,
      issues,
      limits,
      remainingMinutes: this.getRemainingMonthlyMinutes(userTier)
    };
  }

  /**
   * Real-time recording limit enforcement
   * Called during recording to check if limits are being approached
   */
  checkDuringRecording(userTier, currentDurationSec, currentSizeBytes = 0) {
    const limits = this.getTierLimits(userTier);
    const warnings = [];
    const shouldStop = [];

    // Duration warnings and hard stops
    const durationPercent = (currentDurationSec / limits.maxRecordingSec) * 100;

    // RUTHLESS VOICE FIX: Stop 10 seconds BEFORE limit to account for processing delay
    // This prevents backend 413 errors when actual duration calculation exceeds limit
    const SAFETY_BUFFER_SEC = 10;
    if (currentDurationSec >= limits.maxRecordingSec - SAFETY_BUFFER_SEC) {
      shouldStop.push({
        type: 'duration_limit_approaching',
        message: `Approaching maximum recording duration (${this.formatTime(limits.maxRecordingSec)})`,
        action: 'STOP_RECORDING_SAFE'
      });
    } else if (durationPercent >= 90) {
      warnings.push({
        type: 'duration_warning',
        message: `Recording will stop in ${this.formatTime(limits.maxRecordingSec - currentDurationSec)}`,
        percentage: durationPercent
      });
    }

    // File size warnings and hard stops
    if (currentSizeBytes > 0) {
      const sizePercent = (currentSizeBytes / limits.maxFileSize) * 100;

      if (currentSizeBytes >= limits.maxFileSize) {
        shouldStop.push({
          type: 'size_limit_reached',
          message: `Maximum file size reached (${this.formatSize(limits.maxFileSize)})`,
          action: 'STOP_RECORDING'
        });
      } else if (sizePercent >= 90) {
        warnings.push({
          type: 'size_warning',
          message: `Approaching file size limit: ${this.formatSize(currentSizeBytes)}/${this.formatSize(limits.maxFileSize)}`,
          percentage: sizePercent
        });
      }
    }

    // Monthly usage warnings
    if (limits.maxMonthlyMinutes > 0) {
      const monthlyUsedSec = this.currentUsage.monthlyMinutesUsed * 60;
      const totalDurationSec = monthlyUsedSec + currentDurationSec;
      const monthlyPercent = (totalDurationSec / (limits.maxMonthlyMinutes * 60)) * 100;

      if (totalDurationSec >= limits.maxMonthlyMinutes * 60) {
        shouldStop.push({
          type: 'monthly_limit_reached',
          message: 'Monthly recording limit reached',
          action: 'STOP_RECORDING'
        });
      } else if (monthlyPercent >= 90) {
        warnings.push({
          type: 'monthly_warning',
          message: `${Math.round(100 - monthlyPercent)}% of monthly limit remaining`,
          percentage: monthlyPercent
        });
      }
    }

    return {
      warnings,
      shouldStop: shouldStop.length > 0,
      stopReasons: shouldStop,
      limits,
      currentUsage: {
        durationSec: currentDurationSec,
        sizeBytes: currentSizeBytes,
        durationPercent,
        sizePercent: currentSizeBytes > 0 ? (currentSizeBytes / limits.maxFileSize) * 100 : 0
      }
    };
  }

  /**
   * Record usage after successful recording
   */
  recordUsage(userTier, durationSec, sizeBytes) {
    try {
      // Reset monthly usage if new month
      const currentMonth = new Date().getMonth();
      if (this.currentUsage.lastReset !== currentMonth) {
        this.currentUsage.monthlyMinutesUsed = 0;
        this.currentUsage.lastReset = currentMonth;
      }

      // Add to monthly usage
      const minutes = Math.ceil(durationSec / 60);
      this.currentUsage.monthlyMinutesUsed += minutes;

      // Save to localStorage
      this.saveUsageToStorage();

      console.log(`[TierLimits] Recorded usage: ${minutes} minutes (Total this month: ${this.currentUsage.monthlyMinutesUsed})`);

      return {
        minutesUsed: minutes,
        monthlyTotal: this.currentUsage.monthlyMinutesUsed,
        remainingThisMonth: this.getRemainingMonthlyMinutes(userTier)
      };
    } catch (error) {
      console.error('[TierLimits] Failed to record usage:', error);
      return null;
    }
  }

  /**
   * Get tier limits for a given tier
   * SIMPLIFIED: Only free and pro, with deprecated tier mapping
   */
  getTierLimits(tier) {
    // Handle deprecated tiers
    if (tier === 'starter') tier = 'free';
    if (tier === 'max') tier = 'pro';
    return this.TIER_LIMITS[tier] || this.TIER_LIMITS.free;
  }

  /**
   * Get remaining monthly minutes for tier
   */
  getRemainingMonthlyMinutes(tier) {
    const limits = this.getTierLimits(tier);

    if (limits.maxMonthlyMinutes === -1) {
      return -1; // Unlimited
    }

    // Reset if new month
    const currentMonth = new Date().getMonth();
    if (this.currentUsage.lastReset !== currentMonth) {
      this.currentUsage.monthlyMinutesUsed = 0;
      this.currentUsage.lastReset = currentMonth;
      this.saveUsageToStorage();
    }

    return Math.max(0, limits.maxMonthlyMinutes - this.currentUsage.monthlyMinutesUsed);
  }

  /**
   * Get usage summary for UI display
   */
  getUsageSummary(tier) {
    const limits = this.getTierLimits(tier);
    const remaining = this.getRemainingMonthlyMinutes(tier);

    return {
      tier,
      limits: {
        maxRecordingMinutes: Math.round(limits.maxRecordingSec / 60),
        maxFileSizeMB: Math.round(limits.maxFileSize / (1024 * 1024)),
        maxMonthlyMinutes: limits.maxMonthlyMinutes === -1 ? 'Unlimited' : limits.maxMonthlyMinutes
      },
      usage: {
        monthlyMinutesUsed: this.currentUsage.monthlyMinutesUsed,
        remainingMinutes: remaining === -1 ? 'Unlimited' : remaining,
        usagePercent: limits.maxMonthlyMinutes === -1 ? 0 :
                     Math.round((this.currentUsage.monthlyMinutesUsed / limits.maxMonthlyMinutes) * 100)
      },
      features: limits.features
    };
  }

  /**
   * Check if user has access to a specific feature
   */
  hasFeature(tier, feature) {
    const limits = this.getTierLimits(tier);
    return limits.features.includes(feature);
  }

  /**
   * Get upgrade suggestions when limits are hit
   * SIMPLIFIED: Only free → pro upgrade path
   */
  getUpgradeSuggestions(currentTier, issueType) {
    // Normalize deprecated tiers
    if (currentTier === 'starter') currentTier = 'free';
    if (currentTier === 'max') currentTier = 'pro';

    const suggestions = {
      free: {
        target: 'pro',
        benefits: [
          '60-minute recordings',
          '24 hours voice/month',
          'Up to 3 devices',
          'Export & 1-click restore',
          'Enhanced transcription'
        ]
      },
      pro: {
        target: null, // Already highest tier
        benefits: [
          '2-hour recordings (vs 75 minutes)',
          'Unlimited monthly usage',
          'Priority processing',
          'Advanced features'
        ]
      }
    };

    return suggestions[currentTier] || null;
  }

  // Private helper methods
  loadUsageFromStorage() {
    try {
      const stored = localStorage.getItem('voiceUsage');
      if (stored) {
        const data = JSON.parse(stored);
        // Validate data structure
        if (data.monthlyMinutesUsed !== undefined && data.lastReset !== undefined) {
          this.currentUsage = data;
        }
      }
    } catch (error) {
      console.error('[TierLimits] Failed to load usage from storage:', error);
      // Reset to default
      this.currentUsage = {
        monthlyMinutesUsed: 0,
        lastReset: new Date().getMonth()
      };
    }
  }

  saveUsageToStorage() {
    try {
      localStorage.setItem('voiceUsage', JSON.stringify(this.currentUsage));
    } catch (error) {
      console.error('[TierLimits] Failed to save usage to storage:', error);
    }
  }

  formatTime(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  formatSize(bytes) {
    const mb = bytes / (1024 * 1024);
    const gb = bytes / (1024 * 1024 * 1024);

    if (gb >= 1) {
      return `${gb.toFixed(1)}GB`;
    } else {
      return `${mb.toFixed(1)}MB`;
    }
  }

  /**
   * Reset usage (for testing or admin purposes)
   */
  resetUsage() {
    this.currentUsage = {
      monthlyMinutesUsed: 0,
      lastReset: new Date().getMonth()
    };
    this.saveUsageToStorage();
    console.log('[TierLimits] Usage reset');
  }

  /**
   * LEGACY: Set max recording duration (all tiers same value)
   * @param {number} maxDurationSec - Max duration in seconds
   */
  setMaxRecordingDuration(maxDurationSec) {
    if (maxDurationSec && maxDurationSec > 0) {
      // Update all tiers to use the same backend-enforced limit
      Object.keys(this.TIER_LIMITS).forEach(tier => {
        this.TIER_LIMITS[tier].maxRecordingSec = maxDurationSec;
      });
      console.log(`[TierLimits] Updated ALL tiers to ${maxDurationSec}s (${maxDurationSec / 60} minutes)`);
    }
  }

  /**
   * Simulate usage for testing
   */
  simulateUsage(minutes) {
    this.currentUsage.monthlyMinutesUsed += minutes;
    this.saveUsageToStorage();
    console.log(`[TierLimits] Simulated ${minutes} minutes usage`);
  }
}

// Export singleton instance
const tierLimitService = new TierLimitService();
export default tierLimitService;